import { AuthTrackingRecord } from "../authTrackingData";
import AuthServicesTable from "./AuthServicesTable";

interface AuthTrackingDetailsProps {
  auth: AuthTrackingRecord;
  resetKey?: string | number | boolean;
}

const AuthTrackingDetails = ({ auth, resetKey }: AuthTrackingDetailsProps) => {
  const attemptLabel =
    auth.attempts === 1 ? "1 attempt" : `${auth.attempts} attempts`;

  return (
    <div className="at-detail-content">
      <div className="at-detail-summary">
        <div className="at-detail-summary-top">
          <div className="at-detail-summary-heading">
            <h2 className="at-detail-service">{auth.service}</h2>
          </div>
          <div className="at-detail-summary-status">
            <span className={`at-status at-status--${auth.authStatusTone}`}>
              {auth.authStatus}
            </span>
            <span
              className={`at-detail-status-dot at-status--${auth.deliveryTone}`}
              aria-hidden="true"
            />
            <span className={`at-status at-status--${auth.deliveryTone}`}>
              {auth.delivery}
            </span>
          </div>
        </div>
        <p className="at-detail-claim-id" title={auth.claimId}>
          {auth.claimId}
        </p>
        <div className="at-detail-grid">
          <div className="at-detail-field">
            <span className="at-detail-label">EHR organization</span>
            <span className="at-detail-value" title={auth.ehrOrganization}>
              {auth.ehrOrganization}
            </span>
          </div>
          <div className="at-detail-field">
            <span className="at-detail-label">Health plan</span>
            <span className="at-detail-value" title={auth.payer}>
              {auth.payer}
            </span>
          </div>
          <div className="at-detail-field">
            <span className="at-detail-label">Subscription</span>
            <span className="at-detail-value" title={auth.subscription}>
              {auth.subscription}
            </span>
          </div>

          <div className="at-detail-field">
            <span className="at-detail-label">Member</span>
            <span className="at-detail-value" title={auth.memberPlan}>
              {auth.memberPlan}
            </span>
          </div>
          <div className="at-detail-field">
            <span className="at-detail-label">Endpoint</span>
            <span className="at-detail-value" title={auth.endpoint}>
              {auth.endpoint}
            </span>
          </div>
          <div className="at-detail-field">
            <span className="at-detail-label">Delivery attempts</span>
            <span
              className="at-detail-value"
              title={`HTTP ${auth.http} · ${attemptLabel}`}
            >
              HTTP {auth.http} · {attemptLabel}
            </span>
          </div>

          <div className="at-detail-field">
            <span className="at-detail-label">Submitted</span>
            <span
              className="at-detail-value"
              title={`${auth.submittedDate} ${auth.submittedTime}`}
            >
              {auth.submittedDate} {auth.submittedTime}
            </span>
          </div>
          <div className="at-detail-field">
            <span className="at-detail-label">Decision</span>
            <span
              className="at-detail-value"
              title={`${auth.decisionDate} ${auth.decisionTime}`}
            >
              {auth.decisionDate} {auth.decisionTime}
            </span>
          </div>
          <div className="at-detail-field">
            <span className="at-detail-label">Request status</span>
            <span
              className={`at-detail-value at-status at-status--${auth.requestStatusTone}`}
              title={auth.requestStatus}
            >
              {auth.requestStatus}
            </span>
          </div>
          <div className="at-detail-field">
            <span className="at-detail-label">Received date</span>
            <span
              className="at-detail-value"
              title={`${auth.receivedDate} ${auth.receivedTime}`}
            >
              {auth.receivedDate} {auth.receivedTime}
            </span>
          </div>
        </div>
      </div>

      <section className="at-detail-section">
        <AuthServicesTable
          services={auth.cptCodes}
          resetKey={`${auth.id}-${resetKey ?? ""}`}
        />
      </section>

      <section className="at-detail-section">
        <h3 className="at-detail-section-title">Audit trail</h3>
        <ul className="at-audit-trail">
          {auth.auditTrail.map((event) => (
            <li key={event.id} className="at-audit-item">
              <div className="at-audit-card">
                <div className="at-audit-card-title">
                  <span>{event.title}</span>
                  <span
                    className={`at-audit-status at-status--${event.statusTone}`}
                  >
                    <span className="at-audit-status-dot" aria-hidden="true" />
                    <span className="at-audit-status-text">{event.status}</span>
                  </span>
                </div>
                <p className="at-audit-card-desc">
                  {event.description
                    .split(/\s*[·•]\s*/)
                    .map((part, index) => (
                      <span key={`${event.id}-desc-${index}`}>
                        {index > 0 && (
                          <span
                            className={`at-audit-desc-dot at-status--${event.statusTone}`}
                            aria-hidden="true"
                          />
                        )}
                        {part}
                      </span>
                    ))}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default AuthTrackingDetails;
